import React, { useRef, useCallback, useState } from "react";
import { View, StyleSheet, PanResponder } from "react-native";
import { Text } from "@src/packages/medical-history/components/MedicalHistoryText";
import { useMedicalHistoryTheme } from "../../../theme/MedicalHistoryTheme";

interface RangeSliderProps {
  min: number;
  max: number;
  lowValue: number;
  highValue: number;
  onValuesChange: (low: number, high: number) => void;
  lowLabel?: string;
  highLabel?: string;
  /** The value as its bubble shows it, with its unit, such as "7 h". */
  formatValue?: (value: number) => string;
}

const THUMB_SIZE = 28;
// Wide enough for a two-digit value with its unit, such as "10 h".
const BUBBLE_WIDTH = 44;
const TRACK_HEIGHT = 8;

export function RangeSlider({
  min,
  max,
  lowValue,
  highValue,
  onValuesChange,
  lowLabel,
  highLabel,
  formatValue = String,
}: RangeSliderProps) {
  const trackRef = useRef<View>(null);
  const [trackWidth, setTrackWidth] = useState(0);
  const [trackX, setTrackX] = useState(0);
  const [dragging, setDragging] = useState<"low" | "high" | null>(null);
  const theme = useMedicalHistoryTheme();

  const steps = max - min;

  const valueToPosition = (value: number) => {
    if (trackWidth === 0) return 0;
    return ((value - min) / steps) * trackWidth;
  };

  const positionToValue = useCallback(
    (x: number) => {
      if (trackWidth === 0) return min;
      const clamped = Math.max(0, Math.min(x, trackWidth));
      const raw = min + (clamped / trackWidth) * steps;
      return Math.round(raw);
    },
    [trackWidth, min, steps],
  );

  const onTrackLayout = () => {
    trackRef.current?.measureInWindow((x, _y, width) => {
      setTrackX(x);
      setTrackWidth(width);
    });
  };

  const lowPos = valueToPosition(lowValue);
  const highPos = valueToPosition(highValue);

  // Dots at each integer step
  const dots = [];
  for (let i = min; i <= max; i++) {
    dots.push(i);
  }

  const lowPanResponder = PanResponder.create({
    onStartShouldSetPanResponder: () => true,
    onMoveShouldSetPanResponder: () => true,
    onPanResponderGrant: () => setDragging("low"),
    onPanResponderMove: (_e, gestureState) => {
      const x = gestureState.moveX - trackX;
      const newVal = positionToValue(x);
      if (newVal < highValue) {
        onValuesChange(newVal, highValue);
      }
    },
    onPanResponderRelease: () => setDragging(null),
  });

  const highPanResponder = PanResponder.create({
    onStartShouldSetPanResponder: () => true,
    onMoveShouldSetPanResponder: () => true,
    onPanResponderGrant: () => setDragging("high"),
    onPanResponderMove: (_e, gestureState) => {
      const x = gestureState.moveX - trackX;
      const newVal = positionToValue(x);
      if (newVal > lowValue) {
        onValuesChange(lowValue, newVal);
      }
    },
    onPanResponderRelease: () => setDragging(null),
  });

  return (
    <View style={styles.container}>
      {/* Value labels above thumbs */}
      <View style={styles.labelsRow}>
        <View
          style={[
            styles.valueBubble,
            {
              left: lowPos - BUBBLE_WIDTH / 2,
              backgroundColor: theme.optionBg,
              borderColor: theme.inputBorder,
            },
          ]}
        >
          <Text style={{ fontSize: 14, fontWeight: "700", color: theme.text }}>
            {formatValue(lowValue)}
          </Text>
        </View>
        <View
          style={[
            styles.valueBubble,
            {
              left: highPos - BUBBLE_WIDTH / 2,
              backgroundColor: theme.optionBg,
              borderColor: theme.inputBorder,
            },
          ]}
        >
          <Text style={{ fontSize: 14, fontWeight: "700", color: theme.text }}>
            {formatValue(highValue)}
          </Text>
        </View>
      </View>

      {/* Track */}
      <View
        ref={trackRef}
        style={styles.trackContainer}
        onLayout={onTrackLayout}
      >
        {/* Background track */}
        <View style={[styles.track, { backgroundColor: theme.inputBorder }]}>
          {/* Step dots */}
          {dots.map((step) => {
            const dotPos = valueToPosition(step);
            return (
              <View
                key={step}
                style={[
                  styles.dot,
                  { left: dotPos - 3, backgroundColor: theme.inputBorder },
                ]}
              />
            );
          })}

          {/* Active range */}
          <View
            style={[
              styles.activeRange,
              {
                left: lowPos,
                width: Math.max(0, highPos - lowPos),
                backgroundColor: theme.accentLight,
              },
            ]}
          />
        </View>

        {/* Low thumb */}
        <View
          {...lowPanResponder.panHandlers}
          style={[
            styles.thumb,
            {
              left: lowPos - THUMB_SIZE / 2,
              backgroundColor: theme.optionSelectedBg,
              borderColor:
                dragging === "low" ? theme.accentLight : theme.buttonBorder,
            },
          ]}
        >
          <View
            style={[styles.thumbInner, { backgroundColor: theme.accentLight }]}
          />
        </View>

        {/* High thumb */}
        <View
          {...highPanResponder.panHandlers}
          style={[
            styles.thumb,
            {
              left: highPos - THUMB_SIZE / 2,
              backgroundColor: theme.optionSelectedBg,
              borderColor:
                dragging === "high" ? theme.accentLight : theme.buttonBorder,
            },
          ]}
        >
          <View
            style={[styles.thumbInner, { backgroundColor: theme.accentLight }]}
          />
        </View>
      </View>

      {/* Min/Max labels */}
      <View style={styles.rangeLabels}>
        <Text style={{ fontSize: 12, color: theme.textFaint }}>
          {lowLabel ?? `${min}`}
        </Text>
        <Text style={{ fontSize: 12, color: theme.textFaint }}>
          {highLabel ?? `${max}`}
        </Text>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: "100%",
    paddingHorizontal: 4,
  },
  labelsRow: {
    height: 36,
    position: "relative",
    marginBottom: 4,
  },
  valueBubble: {
    position: "absolute",
    top: 0,
    width: BUBBLE_WIDTH,
    height: 32,
    borderRadius: 8,
    borderWidth: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  trackContainer: {
    height: THUMB_SIZE + 8,
    justifyContent: "center",
    position: "relative",
  },
  track: {
    height: TRACK_HEIGHT,
    borderRadius: TRACK_HEIGHT / 2,
    position: "relative",
    overflow: "hidden",
  },
  dot: {
    position: "absolute",
    top: (TRACK_HEIGHT - 6) / 2,
    width: 6,
    height: 6,
    borderRadius: 3,
    zIndex: 1,
  },
  activeRange: {
    position: "absolute",
    top: 0,
    height: "100%",
    borderRadius: TRACK_HEIGHT / 2,
    zIndex: 0,
  },
  thumb: {
    position: "absolute",
    top: (THUMB_SIZE + 8 - THUMB_SIZE) / 2,
    width: THUMB_SIZE,
    height: THUMB_SIZE,
    borderRadius: THUMB_SIZE / 2,
    borderWidth: 2,
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.3,
    shadowRadius: 4,
    elevation: 4,
  },
  thumbInner: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  rangeLabels: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 4,
  },
});
